import { LineItem } from '~api/som';

import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button, Card, FormControl, FormItem, Modal, MultiInputComposite, TextInput } from '@ingrid/luterkort-react';

import { CurrencySelect } from '~src/components/CurrencySelect/CurrencySelect';
import { DimensionFields } from '~src/components/DimensionFields/DimensionFields';
import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { NumberInput } from '~src/components/NumberInput/NumberInput';
import { useTranslation } from '~src/i18n/use-translation';
import { fromAPIAmount, toAmountAPIUpsert } from '~src/utils/amount';

type Mode = 'add' | 'edit';

interface Props {
  onSubmit: (values: LineItem) => void;
  trigger: ReactNode;
  defaultValues?: LineItem;
  mode: Mode;
}

export function LineItemFormDialog({ mode, defaultValues, onSubmit: onSubmitProp, trigger }: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  const { control, formState, watch, register, handleSubmit, reset } = useForm<LineItem>({
    defaultValues: {
      price: fromAPIAmount({ value: defaultValues?.price, currency: defaultValues?.currency }) ?? 0,
      hs_tariff_number: defaultValues?.hs_tariff_number ?? '',
      description: defaultValues?.description ?? '',
      currency: defaultValues?.currency ?? '',
      quantity: defaultValues?.quantity ?? 1,
      weight: defaultValues?.weight ?? 0,
      tags: defaultValues?.tags ?? [],
      name: defaultValues?.name ?? '',
      sku: defaultValues?.sku ?? '',
      dimensions: {
        height: defaultValues?.dimensions?.height ?? 0,
        length: defaultValues?.dimensions?.length ?? 0,
        width: defaultValues?.dimensions?.width ?? 0,
      },
    },
  });
  const { t } = useTranslation();

  function onSubmit(values: LineItem) {
    onSubmitProp({
      ...values,
      price: toAmountAPIUpsert({ value: values.price, currency: values.currency }),
    });

    setModalOpen(false);

    if (mode === 'add') {
      reset();
    }
  }

  function onOpenChange(isOpen: boolean) {
    setModalOpen(isOpen);

    if (!isOpen) {
      reset();
    }
  }

  const price = watch('price');

  return (
    <FormControl.Root>
      <Modal.Root open={modalOpen} onOpenChange={onOpenChange}>
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <Modal.Header title={t(mode === 'add' ? 'common.addLineItem' : 'common.updateLineItem')} />

          <Modal.Body>
            <Card.List css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
              <FormItem.Root css={{ gridColumn: 'span 12' }}>
                <FormItem.Label>{t('common.name')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.name}>
                  <TextInput aria-required="true" {...register('name', { required: true })} />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 12' }}>
                <FormItem.Label>SKU</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.sku}>
                  <TextInput aria-required="true" {...register('sku', { required: true })} />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 2' }}>
                <FormItem.Label>{t('common.quantity')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.quantity}>
                  <NumberInput
                    {...register('quantity', { valueAsNumber: true, required: true, min: 1 })}
                    data-invalid={!!formState.errors.quantity}
                    decimals={false}
                    placeholder="0"
                  />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 3' }}>
                <FormItem.Label>{t('common.weight')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.weight}>
                  <NumberInput
                    {...register('weight', { valueAsNumber: true, min: 0 })}
                    data-invalid={!!formState.errors.weight}
                    decimals={false}
                    suffix="g"
                  />
                </FormItem.Control>
              </FormItem.Root>
              <div style={{ display: 'flex', gap: '4px', gridColumn: 'span 7' }}>
                <DimensionFields register={register} formState={formState} />
              </div>
              <FormItem.Root css={{ gridColumn: 'span 3' }}>
                <FormItem.Label>{t('common.price')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.price?.message}>
                  <NumberInput
                    {...register('price', { valueAsNumber: true, min: 0 })}
                    data-invalid={!!formState.errors.price}
                    step={0.01}
                  />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 3' }}>
                <FormItem.Label>{t('common.currency')}</FormItem.Label>
                <FormItem.Control>
                  <Controller
                    render={({ field, fieldState: { invalid } }) => <CurrencySelect invalid={invalid} {...field} />}
                    rules={{ required: !!price }}
                    control={control}
                    name="currency"
                  />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 6' }}>
                <FormItem.Label>{t('common.hsTariffNumber')}</FormItem.Label>
                <FormItem.Control>
                  <TextInput {...register('hs_tariff_number')} />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 12' }}>
                <FormItem.Label>{t('common.description')}</FormItem.Label>
                <FormItem.Control>
                  <TextInput {...register('description')} />
                </FormItem.Control>
              </FormItem.Root>
              <FormItem.Root css={{ gridColumn: 'span 12' }}>
                <FormItem.Label>{t('common.tags')}</FormItem.Label>
                <FormItem.Control>
                  <Controller
                    render={({ field, fieldState: { invalid } }) => (
                      <MultiInputComposite
                        placeholder={t('common.tagInputHint')}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        data-invalid={invalid}
                      />
                    )}
                    control={control}
                    name="tags"
                  />
                </FormItem.Control>
              </FormItem.Root>
            </Card.List>
          </Modal.Body>

          <Modal.Footer>
            <Modal.CloseButton>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Modal.CloseButton>

            <FormControl.SubmitButton asChild>
              <Button>{t(mode === 'add' ? 'common.addItem' : 'common.updateItem')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>

          <FormLeaveBlocker formState={formState} />
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
